import * as R from 'ramda';
import Select from 'react-select';
import { connect } from 'react-redux';
import { useRef, useMemo, useState } from 'react';
import { createStructuredSelector } from 'reselect';
// mui
import { useTheme } from '@mui/material';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
// icons-material
import FileDownloadIcon from '@mui/icons-material/CloudDownload';
// features
import { boardRoutine } from '../../features/board/routines';
import { loaderRoutine } from '../../features/loader/routines';
// constants
import * as C from '../../constants';
// utilities
import * as U from '../../utilities';
// components
import { ModalComponent } from '../modal';
import { REPORT_FILTER_PARAMS } from './settings/filter-settings';
// ui
import { Box, RelativeBox, AbsoluteBox } from '../../ui';
// component report-format-panel
import EditReport from '../edit-report';
import { ReportWrapper, getDefaultStyles } from './ui';
import { makeSelectUsedReport, makeSelectAvailableReports } from './selectors';
import {
  addUsedReportRoutine,
  editUsedReportRoutine,
  deleteUsedReportRoutine,
} from './routines';
//////////////////////////////////////////////////

const renderOptions = R.map(option => R.mergeRight({
  [C.FIELD_VALUE]: R.path([C.FIELD_UUID], option),
  [C.FIELD_LABEL]: R.path([C.FIELD_NAME], option),
}, option));

const ReportSelect = ({
  width,
  margin,
  options,
  placeholder,
  defaultValue,
  handleChangeUsedReport,
}) => {
  const ref = useRef(null);

  const { colors, palette } = useTheme();

  const {
    input,
    option,
    control,
    menuList,
    container,
    singleValue,
    DropdownIndicator,
    indicatorSeparator,
  } = getDefaultStyles();

  const styles = useMemo(() => ({
    container: provided => ({
      ...provided,
      ...container,
      fontSize: 13,
      display: 'flex',
      margin: '0 5px',
      alignSelf: 'center',
      pointerEvents: null,
      justifyContent: 'center',
    }),

    control: provided => ({
      ...provided,
      ...control,
      minHeight: 30,
      maxHeight: 30,
      width: '100%',
      border: 'none',
      boxShadow: 'none',
      background: palette.background.default,
    }),

    singleValue: provided => ({
      ...provided,
      ...singleValue,
    }),

    indicatorSeparator: provided => ({
      ...provided,
      ...indicatorSeparator,
    }),

    indicatorsContainer: provided => ({
      ...provided,
      height: 30,
      paddingRight: '10px',
    }),

    option: (base, state) => ({
      ...base,
      ...option(base, state),
      minHeight: 30,
      maxWidth: '200px',
      whiteSpace: 'break-spaces',
      wordBreak: 'break-word !important',
    }),

    input: provided => ({
      ...provided,
      ...input,
      maxWidth: '20px',
    }),

    menuList: base => ({
      ...base,
      ...menuList,
      maxHeight: 315,
    }),
  }), [colors]) // eslint-disable-line

  return (
    <RelativeBox>
      <AbsoluteBox bottom='calc(100% - 2px)'>
        <Typography
          mb='3px'
          ml='25px'
          fontSize={9}
          color={colors.darkGrey}
        >
          {U.getTranslation('labels.selectReport', 'Select Report')}
        </Typography>
      </AbsoluteBox>
      <Box margin={margin} width={R.or(width, '232px')}>
        <Select
          ref={ref}
          styles={styles}
          name='report-panel'
          value={defaultValue}
          placeholder={placeholder}
          options={renderOptions(options)}
          onChange={handleChangeUsedReport}
          components={{ DropdownIndicator }}
        />
      </Box>
    </RelativeBox>
  );
};

const ReportFormatPanel = props => {
  const {
    options,
    usedReport,
    selectAction,
    handleChangeUsedReport,
  } = props;

  const theme = useTheme();
  const [opened, setOpened] = useState(false);

  const handleCloseModal = () => setOpened(false);

  const availableFields = R.compose(
    R.map(field => R.assoc(C.FIELD_LABEL, U.getTranslation(`labels.${R.prop(C.FIELD_LABEL, field)}`), field)),
    R.sortBy(R.prop(C.FIELD_LABEL)),
  )(REPORT_FILTER_PARAMS);

  const reportButtonTitle = U.ifElse(
    U.isNilOrEmpty(usedReport),
    U.getTranslation('actions.addReport', 'Add Report'),
    U.getTranslation('actions.editReport', 'Edit Report'),
  );

  return (
    <ReportWrapper>
      <Box
        fontSize={14}
        cursor='pointer'
        fontWeight={500}
        marginRight='5px'
        hoveredOpacity={0.5}
        title={reportButtonTitle}
        color={theme.colors.darkGreen}
        onClick={() => setOpened(true)}
      >
        {reportButtonTitle}
      </Box>
      <ReportSelect
        width='200px'
        theme={theme}
        options={R.or(options, [])}
        selectAction={selectAction}
        handleChangeUsedReport={handleChangeUsedReport}
        placeholder={U.getTranslation('labels.selectReport', 'Select Report')}
        defaultValue={{
          [C.FIELD_LABEL]: R.pathOr('', [C.FIELD_NAME], usedReport),
          [C.FIELD_VALUE]: R.pathOr('', [C.FIELD_UUID], usedReport),
        }}
      />
      <IconButton
        component='a'
        target='__blank'
        sx={{ ml: '20px' }}
        data-testid='testid-Header-downloadFile-icon'
        href={U.downloadFileRequest(R.path([C.FIELD_UUID], usedReport))}
        title={U.getTranslation('actions.downloadFile', 'Download File')}
      >
        <FileDownloadIcon cursor='pointer' sx={{ fontSize: '35px', color: t => t.colors.darkGreen }} />
      </IconButton>
      <ModalComponent
        opened={opened}
        handleClose={handleCloseModal}
        componentBoxSxStyles={{ maxHeight: '85vh' }}
        additionalStyles={{ minWidth: '95%', borderRadius: '4px' }}
        modalTitle={`${U.getTranslation('actions.editReport')}: ${R.pathOr('', [C.FIELD_NAME], usedReport)}`}
        component={(
          <EditReport
            {...props}
            theme={theme}
            isLoading={false}
            closeModal={handleCloseModal}
            cancelAction={handleCloseModal}
            availableFields={availableFields}
            reportFilterParams={REPORT_FILTER_PARAMS}
          />
        )}
      />
    </ReportWrapper>
  );
};

const mapStateToProps = state => createStructuredSelector({
  usedReport: makeSelectUsedReport(state),
  options: makeSelectAvailableReports(state),
});

const mapDispatchToProps = dispatch => ({
  removeReportRequest: id => dispatch(deleteUsedReportRoutine(id)),
  createReportRequest: values => dispatch(addUsedReportRoutine(U.transformUsedReportValues(values))),
  updateReportRequest: values => dispatch(editUsedReportRoutine(U.transformUsedReportValues(values))),
  handleChangeUsedReport: event => {
    const { uuid } = event;

    const reqBody = { [C.FIELD_UUID]: uuid };

    dispatch(loaderRoutine.show());
    dispatch(boardRoutine(reqBody));
    setTimeout(() => dispatch(loaderRoutine.hide()), 2000);
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(ReportFormatPanel);
